import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { usePatient } from "@/context/PatientContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarBlank, Plus, PencilSimple, Trash, Question, ArrowClockwise, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyAppt = { title: "", doctor_name: "", location: "", date: "", duration_minutes: 30, notes: "", follow_up_of: "" };

export default function Appointments() {
  const { activePatient } = usePatient();
  const [appts, setAppts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyAppt);
  const [expandedId, setExpandedId] = useState(null);
  const [newQ, setNewQ] = useState("");

  const load = useCallback(async () => {
    if (!activePatient) return;
    setLoading(true);
    const [a, q] = await Promise.all([
      api.get("/appointments", { params: { patient_id: activePatient.id } }),
      api.get("/questions", { params: { patient_id: activePatient.id } }),
    ]);
    setAppts(a.data.sort((x, y) => (x.date || "").localeCompare(y.date || "")));
    setQuestions(q.data);
    setLoading(false);
  }, [activePatient]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(emptyAppt); setEditingId(null); setOpen(true); };
  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({ ...emptyAppt, ...a, date: a.date ? a.date.slice(0,16) : "" });
    setOpen(true);
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, patient_id: activePatient.id, date: form.date ? new Date(form.date).toISOString() : "" };
      if (editingId) {
        await api.patch(`/appointments/${editingId}`, payload);
        toast.success("Appointment updated");
      } else {
        await api.post("/appointments", payload);
        toast.success("Appointment added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (a) => {
    if (!window.confirm("Delete this appointment?")) return;
    await api.delete(`/appointments/${a.id}`);
    setAppts((xs) => xs.filter((x) => x.id !== a.id));
    toast.success("Deleted");
  };

  const addQuestion = async (apptId) => {
    if (!newQ.trim()) return;
    const { data } = await api.post("/questions", {
      patient_id: activePatient.id,
      text: newQ.trim(),
      appointment_id: apptId,
    });
    setQuestions((qs) => [data, ...qs]);
    setNewQ("");
  };
  const toggleQ = async (q) => {
    const { data } = await api.patch(`/questions/${q.id}`, { answered: !q.answered });
    setQuestions((qs) => qs.map((x) => (x.id === q.id ? data : x)));
  };
  const removeQ = async (q) => {
    await api.delete(`/questions/${q.id}`);
    setQuestions((qs) => qs.filter((x) => x.id !== q.id));
  };
  const scheduleFollowUp = (a) => {
    const d = new Date(a.date || Date.now());
    d.setDate(d.getDate() + 14);
    setForm({
      ...emptyAppt,
      title: `Follow-up: ${a.title || a.doctor_name || ""}`.slice(0, 80),
      doctor_name: a.doctor_name,
      location: a.location,
      date: d.toISOString().slice(0, 16),
      follow_up_of: a.id,
    });
    setEditingId(null);
    setOpen(true);
  };

  if (!activePatient) return <div className="text-[#576A62]">Add a patient first.</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Care calendar</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold">Appointments</h1>
        </div>
        <button
          data-testid="appointments-add-btn"
          onClick={startNew}
          className="inline-flex items-center gap-2 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white px-5 py-3 font-semibold transition-colors"
        >
          <Plus weight="bold" size={16} /> Add appointment
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[0,1,2].map((i) => <div key={i} className="h-24 rounded-2xl bg-[#EBE8E0]/50 animate-pulse" />)}</div>
      ) : appts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#EBE8E0] p-12 text-center bg-white">
          <CalendarBlank size={56} weight="duotone" className="text-[#2C5545] mx-auto" />
          <div className="mt-4 font-heading text-2xl font-bold">No appointments scheduled</div>
        </div>
      ) : (
        <div className="space-y-3">
          {appts.map((a) => {
            const apptQs = questions.filter((q) => q.appointment_id === a.id);
            const openQs = apptQs.filter((q) => !q.answered);
            const isPast = a.date && new Date(a.date) < new Date();
            return (
              <div key={a.id} data-testid={`appointment-card-${a.id}`} className="bg-white rounded-2xl border border-[#EBE8E0] p-5">
                <div className="flex items-start gap-4">
                  <div className={`shrink-0 w-16 text-center rounded-xl p-3 ${isPast ? "bg-[#EBE8E0]/60" : "bg-[#2C5545] text-white"}`}>
                    <div className="text-xs uppercase font-semibold opacity-80">{a.date ? new Date(a.date).toLocaleString("en", { month: "short" }) : "—"}</div>
                    <div className="text-2xl font-heading font-bold leading-none mt-1">{a.date ? new Date(a.date).getDate() : "—"}</div>
                    <div className="text-xs mt-1 opacity-80">{a.date ? new Date(a.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading text-lg font-bold">{a.title || a.doctor_name || "Appointment"}</h3>
                      {a.follow_up_of && <span className="text-xs bg-[#C86A51]/12 text-[#B05A43] rounded-full px-3 py-0.5">Follow-up</span>}
                      {isPast && <span className="text-xs bg-[#EBE8E0] text-[#576A62] rounded-full px-3 py-0.5">Past</span>}
                    </div>
                    <div className="text-sm text-[#576A62] mt-1">{[a.doctor_name, a.location].filter(Boolean).join(" · ")}</div>
                    {a.notes && <div className="text-sm text-[#576A62] mt-2 line-clamp-2">{a.notes}</div>}
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <button
                        data-testid={`appointment-toggle-questions-${a.id}`}
                        onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
                        className="text-sm inline-flex items-center gap-1 text-[#2C5545] font-semibold hover:underline"
                      >
                        <Question size={16} weight="duotone" />
                        {openQs.length} question{openQs.length !== 1 ? "s" : ""} to ask
                      </button>
                      <button
                        data-testid={`appointment-followup-${a.id}`}
                        onClick={() => scheduleFollowUp(a)}
                        className="text-sm inline-flex items-center gap-1 text-[#576A62] hover:text-[#2C5545]"
                      >
                        <ArrowClockwise size={16} /> Schedule follow-up
                      </button>
                      <button
                        onClick={() => startEdit(a)}
                        data-testid={`appointment-edit-${a.id}`}
                        className="text-sm inline-flex items-center gap-1 text-[#576A62] hover:text-[#2C5545]"
                      >
                        <PencilSimple size={16} /> Edit
                      </button>
                      <button
                        onClick={() => remove(a)}
                        data-testid={`appointment-delete-${a.id}`}
                        className="text-sm inline-flex items-center gap-1 text-[#C86A51] hover:text-[#B05A43]"
                      >
                        <Trash size={16} /> Delete
                      </button>
                    </div>
                  </div>
                </div>

                {expandedId === a.id && (
                  <div className="mt-4 border-t border-[#EBE8E0] pt-4">
                    <div className="text-sm font-semibold mb-2">Questions for this visit</div>
                    <div className="space-y-2">
                      {apptQs.length === 0 && <div className="text-sm text-[#576A62]">No questions yet.</div>}
                      {apptQs.map((q) => (
                        <div key={q.id} className="flex items-center gap-2 group" data-testid={`question-row-${q.id}`}>
                          <button
                            onClick={() => toggleQ(q)}
                            data-testid={`question-toggle-${q.id}`}
                            className={`w-6 h-6 rounded-full border ${q.answered ? "bg-[#3E7B5C] border-[#3E7B5C] text-white" : "border-[#576A62]"} flex items-center justify-center shrink-0`}
                          >
                            {q.answered && <Check size={14} weight="bold" />}
                          </button>
                          <span className={`flex-1 text-sm ${q.answered ? "line-through text-[#576A62]" : "text-[#1A2520]"}`}>{q.text}</span>
                          <button
                            onClick={() => removeQ(q)}
                            data-testid={`question-delete-${q.id}`}
                            className="opacity-0 group-hover:opacity-100 text-[#C86A51] p-1"
                          ><Trash size={14} /></button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <input
                        value={newQ}
                        onChange={(e) => setNewQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addQuestion(a.id))}
                        placeholder="Add a question to ask the doctor…"
                        data-testid={`question-input-${a.id}`}
                        className="flex-1 h-10 rounded-full border border-[#EBE8E0] px-4 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]"
                      />
                      <button
                        onClick={() => addQuestion(a.id)}
                        data-testid={`question-add-${a.id}`}
                        className="rounded-full bg-[#2C5545] hover:bg-[#224235] text-white px-4 text-sm font-semibold"
                      >Add</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Edit appointment" : "Add appointment"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <TextField label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="appointment-form-title" placeholder="Annual physical" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Doctor" value={form.doctor_name} onChange={(v) => setForm({ ...form, doctor_name: v })} testid="appointment-form-doctor" />
              <TextField label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="appointment-form-location" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Date & time" type="datetime-local" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="appointment-form-date" />
              <TextField label="Duration (min)" type="number" value={form.duration_minutes} onChange={(v) => setForm({ ...form, duration_minutes: v })} testid="appointment-form-duration" />
            </div>
            <TextField textarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="appointment-form-notes" />
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 border border-[#EBE8E0] text-[#576A62] hover:bg-[#EBE8E0]/60">Cancel</button>
              <button data-testid="appointment-form-save" type="submit" className="rounded-full px-5 py-2.5 bg-[#2C5545] hover:bg-[#224235] text-white font-semibold">Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", placeholder, required, testid, textarea }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1.5">{label}{required && <span className="text-[#C86A51]"> *</span>}</div>
      {textarea ? (
        <textarea
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full rounded-xl border border-[#EBE8E0] bg-white px-4 py-3 focus:ring-2 focus:ring-[#2C5545] outline-none"
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          required={required}
          value={value ?? ""}
          onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
          placeholder={placeholder}
          className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-4 focus:ring-2 focus:ring-[#2C5545] outline-none"
        />
      )}
    </label>
  );
}
